import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Kicker, LineReveal, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { DIVISIONS } from "@/data/divisions";

const division = DIVISIONS.garaje;

export default function CiudadGaraje() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);

  return (
    <main className="bg-void" data-testid="page-ciudad-garaje">
      <section
        ref={heroRef}
        className="noise-overlay relative flex min-h-[92vh] items-end overflow-hidden"
      >
        <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
          <img src={division.hero} alt={division.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-void via-void/70 to-void/30" />
        </motion.div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-44 sm:px-6 lg:px-8">
          <Reveal>
            <Kicker color={division.accent}>
              {division.index} — {division.tag}
            </Kicker>
          </Reveal>
          <h1 className="mt-6 font-serif text-4xl font-bold leading-[1.05] tracking-tight text-slate-100 sm:text-5xl lg:text-7xl">
            <LineReveal lines={[division.name]} />
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-6 max-w-xl font-serif text-xl italic text-gold-light/90 sm:text-2xl">
              {division.tagline}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
          <Reveal>
            <Kicker color={division.accent}>La experiencia</Kicker>
            <h2 className="mt-4 font-serif text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
              Fuego, malta y buena mesa.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-base font-light leading-relaxed text-slate-300 sm:text-lg">
              {division.description}
            </p>
            <p className="mt-6 font-mono text-xs tracking-[0.24em] text-slate-500">
              Caracas, Venezuela — www.ciudad-garaje.com
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28" data-testid="ciudad-garaje-gallery">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {division.images.map((img, i) => (
            <Reveal key={img.url} delay={i * 0.08}>
              <figure className="group">
                <div className="clip-frame relative aspect-[4/3] overflow-hidden bg-ink">
                  <img
                    src={img.url}
                    alt={img.title}
                    loading="lazy"
                    className="h-full w-full object-cover brightness-[0.8] saturate-[0.85] transition-all duration-700 ease-out group-hover:scale-105 group-hover:brightness-100 group-hover:saturate-100"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-void/25 to-void/10 transition-opacity duration-500 group-hover:opacity-30" />
                </div>
                <figcaption className="mt-4">
                  <p
                    className="font-mono text-[10px] uppercase tracking-[0.24em]"
                    style={{ color: division.accent }}
                  >
                    {img.category}
                  </p>
                  <p className="mt-1 text-sm text-slate-300">{img.title}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-24 text-center sm:px-6 lg:px-8 lg:py-32">
        <Reveal>
          <Kicker color={division.accent} className="justify-center">
            Reserva tu mesa
          </Kicker>
          <h2 className="mx-auto mt-6 max-w-2xl font-serif text-3xl font-semibold leading-tight text-slate-100 sm:text-4xl lg:text-5xl">
            La mejor gastronomía, en un ambiente inigualable.
          </h2>
          <a
            href={division.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="btn-visitar-web-ciudad-garaje"
            className="group mt-10 inline-flex items-center gap-4 px-12 py-6 font-mono text-sm uppercase tracking-[0.24em] text-void transition-transform duration-300 hover:-translate-y-1"
            style={{ backgroundColor: division.accent, boxShadow: `0 0 60px -12px ${division.accent}` }}
          >
            Visitar la web de Ciudad Garaje
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </Reveal>
      </section>
    </main>
  );
}
